import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Database } from '@/types/supabase';

export type Favorite = Database['public']['Tables']['favorites']['Row'];

/**
 * Provides dict of user's favorited content.
 * example response:
 {
    "meditation:4dcb25bf-650b-4797-a014-7af81ffe5fa6": true,
    "meditation:in-zijn-blijven": true,
    "meditation:aarden-verbinding": true
  }
 */
export function useFavorites() {
  // map IDs to true (non favorited items won't appear here)
  // TODO: refactor to simple set of IDs of user's favorited items
  const [favorites, setFavorites] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadFavorites();
  }, []);

  async function loadFavorites() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('favorites')
      .select('content_id')
      .eq('user_id', user.id);

    if (data) {
      const favMap = data.reduce(
        (acc, fav) => {
          acc[fav.content_id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setFavorites(favMap);
    }
  }

  async function toggleFavorite(contentId: string) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const isFavorite = favorites[contentId];

    if (isFavorite) {
      await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('content_id', contentId);

      const newFavorites = { ...favorites };
      delete newFavorites[contentId];
      setFavorites(newFavorites);
    } else {
      await supabase.from('favorites').insert([
        {
          user_id: user.id,
          content_id: contentId,
        } satisfies Database['public']['Tables']['favorites']['Insert'],
      ]);

      setFavorites({ ...favorites, [contentId]: true });
    }
  }

  return { favorites, toggleFavorite };
}
